import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useUsageHistory } from "@/hooks/useUsageHistory";
import { useTranslation } from "react-i18next";
import { Toast, useToast } from "@/components/base/Toast";
import { planNameToKey } from "@/mocks/plans";
import StatsCards from "./components/StatsCards";
import AutomationPanel from "./components/AutomationPanel";
import BlacklistPanel from "./components/BlacklistPanel";
import TokenDistributionChart from "./components/TokenDistributionChart";
import PlanUsageRadial from "./components/PlanUsageRadial";
import DailyActivityChart from "./components/DailyActivityChart";
import ActivityHeatmap from "./components/ActivityHeatmap";
import KnowledgeBasePanel from "./components/KnowledgeBasePanel";
import WhatsAppSessionPanel from "./components/WhatsAppSessionPanel";
import ChatWindow from "./components/ChatWindow";
import ChatListSidebar from "./components/ChatListSidebar";
import BulkMessagingPanel from "./components/BulkMessagingPanel";
import CrmPipelinePanel from "./components/CrmPipelinePanel";
import CatalogManagerPanel from "./components/CatalogManagerPanel";
import { useWhatsAppChat } from "@/hooks/useWhatsAppChat";
import { useWhatsAppSession } from "@/hooks/useWhatsAppSession";
import { useWhatsAppLabels } from "@/hooks/useWhatsAppLabels";
import { dailyHistory } from "@/mocks/dashboardCharts";
import { useBulkCampaign } from "@/context/BulkCampaignContext";


type Tab = "overview" | "automation" | "knowledge" | "blacklist" | "whatsapp";

export default function Dashboard() {
  const { t } = useTranslation(["dashboard", "common"]);

  const TABS: { key: Tab; label: string; icon: string }[] = [
    { key: "overview", label: t("dashboard.tabs.overview"), icon: "ri-dashboard-3-line" },
    { key: "automation", label: t("dashboard.tabs.automation"), icon: "ri-robot-2-line" },
    { key: "knowledge", label: t("dashboard.tabs.knowledge"), icon: "ri-database-2-line" },
    { key: "blacklist", label: t("dashboard.tabs.blacklist"), icon: "ri-forbid-2-line" },
    { key: "whatsapp", label: t("dashboard.tabs.whatsapp"), icon: "ri-whatsapp-line" },
  ];

  const { user, logout, updateUser } = useAuth();

  const {
    data,
    loading,
    error,
    actionLoading,
    fetchData,
    toggleAutomationStatus,
    updateReplySettings,
    addToBlacklist,
    removeFromBlacklist,
    importCsv,
  } = useDashboardData();

  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBulkMessaging, setShowBulkMessaging] = useState(false);
  const [showCrmPanel, setShowCrmPanel] = useState(false);
  const [showCatalogPanel, setShowCatalogPanel] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  const { toast, showToast, dismissToast } = useToast();

  // Track when session transitions from "no token" to "has token" to force chat refresh
  const prevHadTokenRef = useRef(!!user?.token);
  const initialChatFetchDoneRef = useRef(false);

  const { history, hasHistory, recordSnapshot } = useUsageHistory();

  const sessionName = data?.session || "";
  const {
    chats,
    archivedChats,
    selectedChat,
    messages,
    loadingChats,
    loadingMessages,
    sendingMessage,
    error: chatError,
    filter,
    searchTerm,
    showArchived,
    hasToken: chatHasToken,
    tokenInvalid,
    setFilter,
    setSearchTerm,
    setShowArchived,
    selectChat,
    sendMessage,
    sendReply,
    sendImage,
    sendVoice,
    sendFile,
    sendLocation,
    sendLink,
    reactToMessage,
    downloadMedia,
    sendAiReply,
    aiReplying,
    fetchChats,
    fetchMessages,
    lastMessages,
    hasMoreChats,
    loadMoreChats,
    resolveLidToPhone,
  } = useWhatsAppChat(sessionName);

  const { status: sessionStatus } = useWhatsAppSession(sessionName);

  const {
    isSending,
    isScheduled,
    sendProgress,
    scheduleDate,
    scheduleTime,
    cancelSchedule,
    stopCampaign,
    registerHandlers,
    showSummary,
    summaryStats,
    setShowSummary,
    retryFailedContacts
  } = useBulkCampaign();

  // Register WhatsApp handlers for background syncing
  useEffect(() => {
    registerHandlers({
      sendMessage,
      sendImage,
      sendFile,
      resolveLidToPhone,
    });
  }, [sendMessage, sendImage, sendFile, resolveLidToPhone, registerHandlers]);

  // Draggable floating card state
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only left click
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("i") || target.closest("a")) return;

    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dragOffsetRef.current = { ...dragPos };
    e.preventDefault();
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("i") || target.closest("a")) return;

    setIsDragging(true);
    const touch = e.touches[0];
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    dragOffsetRef.current = { ...dragPos };
  };

  useEffect(() => {
    if (!isDragging) return;

    const limitPosition = (dx: number, dy: number) => {
      let newX = dragOffsetRef.current.x + dx;
      let newY = dragOffsetRef.current.y + dy;

      const card = document.getElementById("floating-campaign-card");
      if (card) {
        const rect = card.getBoundingClientRect();
        const cardWidth = rect.width;
        const cardHeight = rect.height;

        // Constraint bounds: Keep card at least 8px inside viewport edges
        const initialLeft = window.innerWidth - 24 - cardWidth;
        const initialTop = window.innerHeight - 24 - cardHeight;

        const minX = 8 - initialLeft;
        const maxX = window.innerWidth - 8 - initialLeft - cardWidth;
        const minY = 8 - initialTop;
        const maxY = window.innerHeight - 8 - initialTop - cardHeight;

        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));
      }
      return { x: newX, y: newY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setDragPos(limitPosition(dx, dy));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const dx = touch.clientX - dragStartRef.current.x;
      const dy = touch.clientY - dragStartRef.current.y;
      setDragPos(limitPosition(dx, dy));
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging]);




  const isSessionConnected = chatHasToken && !tokenInvalid && sessionStatus === "connected";
  const { tagNewLead, getChatStageAndTags, hasFetched, setupDone } = useWhatsAppLabels(sessionName, isSessionConnected);

  // Auto-tag new chats as "Lead New" when they first appear if not yet assigned to any stage
  useEffect(() => {
    if (!sessionName || !chatHasToken || chats.length === 0 || !hasFetched || !setupDone) return;

    for (const chat of chats) {
      if (!chat?.id?._serialized) continue;
      const { stage } = getChatStageAndTags(chat.id._serialized);
      if (!stage) {
        tagNewLead(chat.id._serialized).catch(() => {});
      }
    }
  }, [chats, sessionName, chatHasToken, hasFetched, setupDone, getChatStageAndTags, tagNewLead]);

  useEffect(() => {
    if (data?.usage) {
      recordSnapshot(data.usage);
    }
  }, [data, recordSnapshot]);

  // Auto-restart scan when token is detected as invalid (401) or session is gone (404)
  useEffect(() => {
    const isInvalidTokenError =
      tokenInvalid ||
      (chatError && /invalid|unauthorized|token|401|not found|404|no longer exists/i.test(chatError)) ||
      (error && /invalid|unauthorized|token|401|not found|404|no longer exists/i.test(error));

    if (isInvalidTokenError && user?.session) {
      showToast(t("dashboard.toast.disconnected"), "warning");
      localStorage.removeItem(`wpp_session_state_${user.session}`);
      updateUser({ token: "" });
    }
  }, [tokenInvalid, chatError, error, user?.session, updateUser, showToast]);

  // When user token transitions from empty to set (session just connected), switch to WhatsApp tab
  // and force chat refresh so the list appears without needing a page reload
  useEffect(() => {
    const nowHasToken = !!user?.token;
    if (!prevHadTokenRef.current && nowHasToken) {
      // Session just connected — switch to WhatsApp tab and schedule chat fetch
      setActiveTab("whatsapp");
      initialChatFetchDoneRef.current = false;
    }
    prevHadTokenRef.current = nowHasToken;
  }, [user?.token]);

  // Force-fetch chats when we land on WhatsApp tab with a token and haven't fetched yet
  useEffect(() => {
    if (activeTab === "whatsapp" && chatHasToken && !initialChatFetchDoneRef.current && sessionName) {
      initialChatFetchDoneRef.current = true;
      // Small delay to ensure socket connection is established
      const timer = setTimeout(() => {
        fetchChats();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, chatHasToken, sessionName, fetchChats]);

  const chartHistory = (hasHistory ? history : dailyHistory).map((item) => ({
    ...item,
    total: "total" in item ? (item as any).total : item.messages,
  }));

  // If the user has no token (e.g. newly registered), we render ONLY the WhatsApp session panel so they can scan QR.
  if (user && !user.token) {
    return (
      <div className="min-h-screen bg-background-50 pt-24 pb-12">
        <div className="px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-0.5">
                {t("dashboard.title")}
              </h1>
              <p className="text-foreground-500 text-sm">
                {t("dashboard.connectPrompt")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-xs text-foreground-400 bg-background-100 px-3 py-1.5 rounded-full">
                {user.phone}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-sm"></i>
                <span className="hidden sm:inline">{t("nav.logout", { ns: "common" })}</span>
              </button>
            </div>
          </div>

          <div className="mt-6">
            <WhatsAppSessionPanel sessionName={user.session} />
          </div>
        </div>
        <Toast toast={toast} onDismiss={dismissToast} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 pt-24 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-foreground-500 text-sm mt-3">{t("dashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isTokenError = /invalid|unauthorized|token|401|not found|404/i.test(error);
    const handleRestartScan = () => {
      if (user?.session) {
        localStorage.removeItem(`wpp_session_state_${user.session}`);
      }
      updateUser({ token: "" });
    };

    return (
      <div className="min-h-screen bg-background-50 pt-24 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-950 mb-2">{t("dashboard.error.title")}</h2>
          <p className="text-foreground-500 text-sm mb-4">{error}</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={fetchData}
              className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("button.tryAgain", { ns: "common" })}
            </button>
            {isTokenError && (
              <button
                onClick={handleRestartScan}
                className="px-5 py-2.5 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-sm mr-1.5"></i>
                {t("dashboard.error.restartScan")}
              </button>
            )}
          </div>
          {isTokenError && (
            <p className="text-foreground-400 text-xs mt-4">
              {t("dashboard.error.needReconnect")}
            </p>
          )}
        </div>
        <Toast toast={toast} onDismiss={dismissToast} />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-background-50 pt-24 pb-12">
      <div className="px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-0.5">
              {t("dashboard.title")}
            </h1>
            <p className="text-foreground-500 text-sm">
              {data.session} &middot; {t(planNameToKey(data.plan.name), { ns: "common" })} {t("dashboard.plan")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-foreground-400 bg-background-100 px-3 py-1.5 rounded-full">
              {user?.phone}
            </span>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100">
              {t(planNameToKey(data.plan.name), { ns: "common" })}
            </span>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
              <span className="hidden sm:inline">{t("nav.logout", { ns: "common" })}</span>
            </button>
          </div>
        </div>

        <div className="md:hidden mb-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className={`${TABS.find((t) => t.key === activeTab)?.icon} text-sm`}></i>
              {TABS.find((t) => t.key === activeTab)?.label}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${mobileMenuOpen ? "rotate-180" : ""}`}></i>
          </button>
          {mobileMenuOpen && (
            <div className="mt-2 bg-background-50 rounded-xl border border-background-200/50 overflow-hidden">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setMobileMenuOpen(false);
                    setMobileChatOpen(false);
                    setShowCrmPanel(false);
                    setShowCatalogPanel(false);
                    setShowBulkMessaging(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
                    activeTab === tab.key
                      ? "bg-primary-50 text-primary-700 font-medium"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-1 mb-6 bg-background-100/50 p-1 rounded-full w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-background-50 text-foreground-950 shadow-sm"
                  : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {activeTab === "overview" && (
            <div className="space-y-5">
              <StatsCards plan={data.plan} usage={data.usage} history={chartHistory} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1">
                  <TokenDistributionChart
                    inputTokens={data.usage?.input ?? 0}
                    outputTokens={data.usage?.output ?? 0}
                  />
                </div>
                <div className="lg:col-span-2">
                  <PlanUsageRadial plan={data.plan} usage={data.usage} />
                </div>
              </div>
              <ActivityHeatmap history={chartHistory} />
              <DailyActivityChart data={chartHistory} />
              <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-3">{t("dashboard.quickInfo.title")}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-xl bg-background-100/60">
                    <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("dashboard.quickInfo.plan")}</p>
                    <p className="text-foreground-950 font-semibold text-sm">{t(planNameToKey(data.plan.name), { ns: "common" })}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-background-100/60">
                    <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("dashboard.quickInfo.session")}</p>
                    <p className="text-foreground-950 font-semibold text-sm font-mono">{data.session}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-background-100/60">
                    <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("dashboard.quickInfo.csvRows")}</p>
                    <p className="text-foreground-950 font-semibold text-sm">{data.csvData.length}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-background-100/60">
                    <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("dashboard.quickInfo.blacklisted")}</p>
                    <p className="text-foreground-950 font-semibold text-sm">{data.blacklist.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "automation" && (
            <AutomationPanel
              automation={data.automation}
              loading={actionLoading === "automation"}
              onToggleStatus={toggleAutomationStatus}
              onUpdateReply={updateReplySettings}
            />
          )}

          {activeTab === "knowledge" && (
            <KnowledgeBasePanel
              csvData={data.csvData}
              loading={actionLoading === "import"}
              phone={data.phone}
              onImport={importCsv}
            />
          )}

          {activeTab === "blacklist" && (
            <BlacklistPanel
              blacklist={data.blacklist}
              loading={actionLoading === "blacklist"}
              onAdd={addToBlacklist}
              onRemove={removeFromBlacklist}
            />
          )}

          {activeTab === "whatsapp" && (
            <div>
              {!chatHasToken || tokenInvalid || sessionStatus === "idle" || sessionStatus === "qr_ready" || sessionStatus === "error" ? (
                <WhatsAppSessionPanel sessionName={data.session} />
              ) : showBulkMessaging ? (
                <div className="min-h-[500px] lg:h-[calc(100vh-240px)] bg-background-50 rounded-2xl border border-background-200/50 lg:overflow-hidden overflow-y-auto p-3 md:p-4">
                  <BulkMessagingPanel
                    chats={chats}
                    csvData={data.csvData}
                    sendMessage={sendMessage}
                    sendImage={sendImage}
                    sendFile={sendFile}
                    resolveLidToPhone={resolveLidToPhone}
                    onBack={() => setShowBulkMessaging(false)}
                  />
                </div>
              ) : (
                <>
                  {/* Mobile: single-pane view */}
                  <div className="lg:hidden flex flex-col h-[calc(100vh-200px)] min-h-[500px] bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">
                    {showCrmPanel ? (
                      <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-2 border-b border-background-200/60 bg-background-50">
                          <button
                            onClick={() => setShowCrmPanel(false)}
                            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                            title={t("chatWindow.back")}
                          >
                            <i className="ri-arrow-left-line text-sm"></i>
                          </button>
                          <span className="font-heading text-sm text-foreground-950">{t("crm.title")}</span>
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <CrmPipelinePanel
                            sessionName={data.session}
                            chats={chats}
                            selectedChat={selectedChat}
                            contacts={data.contacts}
                            onSelectChat={(chat) => {
                              selectChat(chat);
                              setShowCrmPanel(false);
                            }}
                          />
                        </div>
                      </div>
                    ) : showCatalogPanel ? (
                      <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-2 border-b border-background-200/60 bg-background-50">
                          <button
                            onClick={() => setShowCatalogPanel(false)}
                            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                            title={t("chatWindow.back")}
                          >
                            <i className="ri-arrow-left-line text-sm"></i>
                          </button>
                          <span className="font-heading text-sm text-foreground-950">{t("catalog.title")}</span>
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <CatalogManagerPanel sessionName={data.session} phone={data.phone} />
                        </div>
                      </div>
                    ) : !mobileChatOpen ? (
                      <ChatListSidebar
                        chats={chats}
                        archivedChats={archivedChats}
                        selectedChat={selectedChat}
                        loading={loadingChats}
                        error={chatError}
                        filter={filter}
                        searchTerm={searchTerm}
                        showArchived={showArchived}
                        lastMessages={lastMessages}
                        onFilterChange={setFilter}
                        onSearchChange={setSearchTerm}
                        onSelectChat={(chat) => { selectChat(chat); setMobileChatOpen(true); }}
                        onToggleArchived={() => setShowArchived(!showArchived)}
                        onRetry={fetchChats}
                        onBulkClick={() => setShowBulkMessaging(true)}
                        onCrmClick={() => setShowCrmPanel(true)}
                        onCatalogClick={() => setShowCatalogPanel(true)}
                        hasMore={hasMoreChats}
                        onLoadMore={loadMoreChats}
                      />
                    ) : (
                      <ChatWindow
                        chat={selectedChat}
                        messages={messages}
                        loading={loadingMessages}
                        sendingMessage={sendingMessage}
                        error={chatError}
                        onSendMessage={sendMessage}
                        onSendReply={sendReply}
                        onSendImage={sendImage}
                        onSendFile={sendFile}
                        onSendVoice={sendVoice}
                        onSendLocation={sendLocation}
                        onSendLink={sendLink}
                        onReactMessage={reactToMessage}
                        onRetryMessages={fetchMessages}
                        onDownloadMedia={downloadMedia}
                        onAiReply={sendAiReply}
                        aiReplying={aiReplying}
                        onBack={() => setMobileChatOpen(false)}
                      />
                    )}
                  </div>

                  {/* Desktop: side-by-side view */}
                  <div className="hidden lg:flex h-[calc(100vh-240px)] min-h-[500px] bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">
                    <ChatListSidebar
                      chats={chats}
                      archivedChats={archivedChats}
                      selectedChat={selectedChat}
                      loading={loadingChats}
                      error={chatError}
                      filter={filter}
                      searchTerm={searchTerm}
                      showArchived={showArchived}
                      lastMessages={lastMessages}
                      onFilterChange={setFilter}
                      onSearchChange={setSearchTerm}
                      onSelectChat={(chat) => { selectChat(chat); setShowCrmPanel(false); setShowCatalogPanel(false); }}
                      onToggleArchived={() => setShowArchived(!showArchived)}
                      onRetry={fetchChats}
                      onBulkClick={() => setShowBulkMessaging(true)}
                      onCrmClick={() => { setShowCrmPanel(true); setShowCatalogPanel(false); }}
                      onCatalogClick={() => { setShowCatalogPanel(true); setShowCrmPanel(false); }}
                      hasMore={hasMoreChats}
                      onLoadMore={loadMoreChats}
                    />
                    {showCrmPanel ? (
                      <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-2 border-b border-background-200/60 bg-background-50">
                          <button
                            onClick={() => setShowCrmPanel(false)}
                            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                            title={t("chatWindow.back")}
                          >
                            <i className="ri-arrow-left-line text-sm"></i>
                          </button>
                          <span className="font-heading text-sm text-foreground-950">{t("crm.title")}</span>
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <CrmPipelinePanel
                            sessionName={data.session}
                            chats={chats}
                            selectedChat={selectedChat}
                            contacts={data.contacts}
                            onSelectChat={(chat) => {
                              selectChat(chat);
                              setShowCrmPanel(false);
                            }}
                          />
                        </div>
                      </div>
                    ) : showCatalogPanel ? (
                      <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-2 border-b border-background-200/60 bg-background-50">
                          <button
                            onClick={() => setShowCatalogPanel(false)}
                            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                            title={t("chatWindow.back")}
                          >
                            <i className="ri-arrow-left-line text-sm"></i>
                          </button>
                          <span className="font-heading text-sm text-foreground-950">{t("catalog.title")}</span>
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <CatalogManagerPanel sessionName={data.session} phone={data.phone} />
                        </div>
                      </div>
                    ) : (
                    <ChatWindow
                      chat={selectedChat}
                      messages={messages}
                      loading={loadingMessages}
                      sendingMessage={sendingMessage}
                      error={chatError}
                      onSendMessage={sendMessage}
                      onSendReply={sendReply}
                      onSendImage={sendImage}
                      onSendFile={sendFile}
                      onSendVoice={sendVoice}
                      onSendLocation={sendLocation}
                      onSendLink={sendLink}
                      onReactMessage={reactToMessage}
                      onRetryMessages={fetchMessages}
                      onDownloadMedia={downloadMedia}
                      onAiReply={sendAiReply}
                      aiReplying={aiReplying}
                    />
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Floating Background Campaign Progress */}
      {(isSending || isScheduled) && (!showBulkMessaging || activeTab !== "whatsapp") && (
        <div
          id="floating-campaign-card"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{ transform: `translate(${dragPos.x}px, ${dragPos.y}px)` }}
          className={`fixed bottom-6 right-6 z-50 max-w-sm w-full bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-800 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] p-4 flex flex-col gap-3.5 transition-shadow duration-300 select-none ${isDragging ? "cursor-grabbing shadow-[0_20px_50px_rgba(0,0,0,0.18)]" : "cursor-grab"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-drag-move-2-fill text-zinc-400 dark:text-zinc-500 text-sm"></i>
              <span className="relative flex h-2.5 w-2.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isSending ? "bg-emerald-400" : "bg-amber-400"}`}></span>
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isSending ? "bg-emerald-500" : "bg-amber-500"}`}></span>
              </span>
              <span className="font-heading text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">
                {isSending ? t("bulk.sendingActive", { defaultValue: "Bulk Campaign Running" }) : t("bulk.campaignScheduledShort", { defaultValue: "Scheduled Send" })}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  setActiveTab("whatsapp");
                  setShowBulkMessaging(true);
                }}
                className="text-xs font-semibold text-primary-600 dark:text-primary-405 hover:bg-primary-50 dark:hover:bg-primary-950/40 px-2.5 py-1 rounded-lg transition-colors"
              >
                {t("bulk.view", { defaultValue: "View" })}
              </button>
              <button
                onClick={isSending ? stopCampaign : cancelSchedule}
                className="p-1.5 text-zinc-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all"
                title={isSending ? t("bulk.stopCampaign", { defaultValue: "Stop" }) : t("bulk.cancelSchedule", { defaultValue: "Cancel" })}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {isSending ? (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
                <span>{t("bulk.sendingProgress", { defaultValue: "Progress" })}</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-200">{sendProgress.current} / {sendProgress.total}</span>
              </div>
              <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary-500 to-emerald-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${sendProgress.total > 0 ? (sendProgress.current / sendProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-zinc-50 dark:bg-zinc-800/40 rounded-xl border border-zinc-100 dark:border-zinc-800/80 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-500 dark:text-amber-400 flex-shrink-0">
                <i className="ri-calendar-event-line text-lg"></i>
              </div>
              <div className="text-xs flex flex-col gap-0.5 min-w-0">
                <span className="text-zinc-400 dark:text-zinc-500">{t("bulk.scheduledFor", { defaultValue: "Scheduled for" })}:</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-200 truncate">
                  {(() => {
                    try {
                      const d = new Date(`${scheduleDate}T${scheduleTime}`);
                      if (isNaN(d.getTime())) return `${scheduleDate} • ${scheduleTime}`;
                      return d.toLocaleDateString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      }) + " • " + d.toLocaleTimeString(undefined, {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    } catch {
                      return `${scheduleDate} • ${scheduleTime}`;
                    }
                  })()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign Completion Summary Modal */}
      {showSummary && summaryStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background-950/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-3xl shadow-2xl max-w-md w-full p-6 flex flex-col gap-4 transform scale-100 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-zinc-100 dark:border-zinc-800">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${summaryStats.failed === 0 ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400" : "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400"}`}>
                  <i className={summaryStats.failed === 0 ? "ri-checkbox-circle-fill text-xl" : "ri-error-warning-fill text-xl"}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-zinc-800 dark:text-zinc-100">
                    {summaryStats.failed === 0 
                      ? t("bulk.summaryTitleSuccess", { defaultValue: "Campaign Finished!" })
                      : t("bulk.summaryTitleWarning", { defaultValue: "Campaign Completed with Issues" })}
                  </h3>
                  <span className="text-[11px] text-zinc-400 dark:text-zinc-500 uppercase font-semibold tracking-wider">
                    {t("bulk.summarySubtitle", { defaultValue: "Delivery Status" })}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowSummary(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-400 hover:text-zinc-650 dark:hover:text-zinc-300 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 my-2 text-center">
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800/40 rounded-2xl border border-zinc-100/50 dark:border-zinc-800">
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500 font-semibold block mb-0.5">{t("bulk.summaryTotal", { defaultValue: "Recipients" })}</span>
                <span className="font-heading text-lg font-bold text-zinc-800 dark:text-zinc-200">{summaryStats.total}</span>
              </div>
              <div className="p-3 bg-emerald-50/40 dark:bg-emerald-950/10 rounded-2xl border border-emerald-100/30 dark:border-emerald-900/10">
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold block mb-0.5">{t("bulk.summarySent", { defaultValue: "Sent" })}</span>
                <span className="font-heading text-lg font-bold text-emerald-600 dark:text-emerald-400">{summaryStats.sent}</span>
              </div>
              <div className="p-3 bg-red-50/40 dark:bg-red-950/10 rounded-2xl border border-red-100/30 dark:border-red-900/10">
                <span className="text-[10px] text-red-650 dark:text-red-450 font-semibold block mb-0.5">{t("bulk.summaryFailed", { defaultValue: "Failed" })}</span>
                <span className="font-heading text-lg font-bold text-red-650 dark:text-red-400">{summaryStats.failed}</span>
              </div>
            </div>

            {summaryStats.failed > 0 && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 bg-red-50/30 dark:bg-red-950/5 border border-red-100/30 dark:border-red-900/10 p-3 rounded-xl leading-relaxed">
                {t("bulk.summaryRetryHelp", { 
                  defaultValue: "Some messages could not be sent. You can retry sending to only the failed contacts." 
                })}
              </p>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowSummary(false)}
                className="flex-1 h-10.5 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 text-xs font-semibold text-zinc-650 dark:text-zinc-300 transition-colors"
              >
                {t("common.close", { defaultValue: "Dismiss" })}
              </button>
              {summaryStats.failed > 0 && (
                <button
                  onClick={retryFailedContacts}
                  className="flex-1 h-10.5 rounded-xl bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-lg shadow-accent-600/15"
                >
                  <i className="ri-refresh-line"></i>
                  {t("bulk.summaryRetryBtn", { defaultValue: "Retry Failed" })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}